import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@/hooks/use-toast';
import { useDeleteDevicesByIdMutation } from '@/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface DeleteDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
}

export function DeleteDeviceModal({ isOpen, onClose, deviceId }: DeleteDeviceModalProps) {
  const navigate = useNavigate();
  const [deleteDevice, { isLoading }] = useDeleteDevicesByIdMutation();
  
  const handleDelete = async () => {
    try {
      await deleteDevice({ id: deviceId }).unwrap();
      
      toast({
        title: 'Success',
        description: 'Device deleted successfully',
      });
      
      // Navigate back to devices list
      navigate({ to: '/dashboard/devices' });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to delete device',
      });
      onClose();
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Device</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this device? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center justify-center py-4">
          <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-full">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete} 
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete Device'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}